/**
 * Q1 weapon-fire recoil — pure math.
 *
 * On the rising edge of `justFired`, the chassis snaps backward + tilts
 * nose-up briefly, then recovers. Curve is "kick-and-decay" — a fast
 * linear attack over the first ~20% of the window followed by a
 * cosine-eased return — so the impulse reads as a single thump rather
 * than a symmetric wobble.
 *
 * Two channels:
 *   - {dx, dz}  — XZ push along the negated forward vector.
 *   - pitchX    — rotation around the chassis local-X axis (nose-up).
 *
 * Pure functions; no Three deps so it unit-tests cleanly.
 */

export const RECOIL_DURATION_MS = 150;
export const RECOIL_DISTANCE = 0.1;
/** Peak nose-up pitch (radians). ~0.08 rad ≈ 4.6°. */
export const RECOIL_PITCH_RAD = 0.08;
/** Fraction of the window spent in attack phase (linear 0 → 1). */
const ATTACK_FRACTION = 0.18;

export interface RecoilOffset {
  dx: number;
  dz: number;
}

const REST: RecoilOffset = { dx: 0, dz: 0 };

/**
 * Normalized envelope (0..1) for elapsedMs inside the recoil window.
 * - Attack: 0..ATTACK_FRACTION linear ramp to 1.
 * - Decay : ATTACK_FRACTION..1 cosine ease back to 0.
 * Returns 0 outside the window.
 */
export function recoilEnvelope(elapsedMs: number | null): number {
  if (elapsedMs === null || elapsedMs < 0 || elapsedMs >= RECOIL_DURATION_MS) return 0;
  const t = elapsedMs / RECOIL_DURATION_MS;
  if (t < ATTACK_FRACTION) return t / ATTACK_FRACTION;
  // Cosine ease 1 → 0 over the remaining window.
  const u = (t - ATTACK_FRACTION) / (1 - ATTACK_FRACTION);
  return 0.5 * (1 + Math.cos(u * Math.PI));
}

/**
 * Recoil XZ offset for a mech facing world-yaw `yaw` whose most-recent
 * fire-flash started `elapsedMs` ago. Forward vector convention matches
 * `facingToYaw`: at yaw=0 forward is +Z, so backward = -Z.
 */
export function recoilOffset(elapsedMs: number | null, yaw: number): RecoilOffset {
  const env = recoilEnvelope(elapsedMs);
  if (env === 0) return REST;
  const magnitude = RECOIL_DISTANCE * env;
  return {
    dx: -magnitude * Math.sin(yaw),
    dz: -magnitude * Math.cos(yaw),
  };
}

/**
 * Nose-up pitch (radians) at `elapsedMs` into the recoil window.
 * Positive = nose tilts up. Applied around the chassis local-X axis so
 * the rotation reads as a barrel-lift regardless of yaw.
 */
export function recoilPitch(elapsedMs: number | null): number {
  return RECOIL_PITCH_RAD * recoilEnvelope(elapsedMs);
}
